import { findAndReplace } from '@/utils';
import { showErrorToast } from '~/utils/toast-notifications';
import { HOME_FEED_TAB_KEYS } from '~/utils/home-feed-tab-keys';

export default {
  SET_ACTIVE_FEED_TAB: (state, payload) => {
    state.activeFeedTab = payload;
  },
  GET_FEED_START: (state) => {
    state.homeForYouFeed = [];
    state.newPostNumber = null;
    state.error = null;
    state.homeForYouFeedLoading = true;
  },
  GET_FEED_SUCCESS: (state, result) => {
    const { data, cursor } = result;

    state.homeForYouFeedCursor = cursor;
    state.homeForYouFeed = data;
    state.homeForYouFeedLoading = false;
  },
  GET_FEED_ERROR: (state, error) => {
    state.error = error;
    state.homeForYouFeedLoading = false;
  },
  COUNT_NEW_POST_SUCCESS: (state, data) => {
    state.newPostNumber = data;
  },
  SET_ERROR: (state, error) => {
    state.error = error;
    showErrorToast(window.$nuxt.$t('error_home_page'));
  },
  UPDATE_POST: (state, payload) => {
    state.homeForYouFeed = findAndReplace(
      state.homeForYouFeed,
      'postId',
      payload.postId,
      payload
    );
  },
  UPDATE_POST_AFTER_EDIT: (state, payload) => {
    const postIndex = state.homeForYouFeed.findIndex(
      (e) => e.postId === payload.postId
    );
    if (postIndex !== -1) {
      state.homeForYouFeed.splice(postIndex, 1, payload);
    }
  },
  UPDATE_REPLY_AFTER_EDIT: (state, payload) => {
    const post = state.homeForYouFeed.find((e) => e.postId === payload.postId);
    if (post) {
      const replyIndex = post.replies.findIndex(
        (e) => e.postId === payload.data.target
      );

      if (replyIndex !== -1) {
        if (post.replies[replyIndex].replyCount)
          payload.data.replyCount = post.replies[replyIndex].replyCount;
        if (post.replies[replyIndex].replies)
          payload.data.replies = post.replies[replyIndex].replies;

        post.replies.splice(replyIndex, 1, payload.data);
      }
    } else {
      const parentPost = state.homeForYouFeed.find((p) =>
        p.replies?.some((r) => r.postId === payload.postId)
      );
      if (!parentPost) return;
      const parentPostIndex = state.homeForYouFeed.findIndex((p) =>
        p.replies?.some((r) => r.postId === payload.postId)
      );
      const reply = parentPost?.replies?.find(
        (r) => r.postId === payload.postId
      );
      if (!reply) return;
      const replyIndex = parentPost?.replies?.findIndex(
        (r) => r.postId === payload.postId
      );
      const nestedReplyIndex = reply.replies.findIndex(
        (nr) => nr.postId === payload.data.postId
      );
      if (nestedReplyIndex !== -1) {
        payload.data.target = payload.postId;
        reply.replies[nestedReplyIndex] = payload.data;
        parentPost.replies[replyIndex] = reply;
        state.homeForYouFeed.splice(parentPostIndex, 1, parentPost);
      }
    }
  },
  UPDATE_NESTED_REPLY_AFTER_EDIT: (state, payload) => {
    const post = state.homeForYouFeed.find((e) => e.postId === payload.postId);
    if (post) {
      const replyIndex = post.replies.findIndex(
        (e) => e.postId === payload.replyId
      );
      if (replyIndex !== -1) {
        const nestedReplies = post.replies[replyIndex].replies;
        const nestedReplyIndex = nestedReplies.findIndex(
          (e) => e.postId === payload.nestedReplyId
        );
        const nestedReply = nestedReplies[nestedReplyIndex];
        if (nestedReply?.replyCount)
          payload.data.replyCount = nestedReply.replyCount;
        if (nestedReply?.replies) payload.data.replies = nestedReply.replies;
        if (nestedReplyIndex !== -1) {
          nestedReplies.splice(nestedReplyIndex, 1, payload.data);
        }
      }
    }
  },
  INCREMENT_POST_REPLY_COUNT: (state, postId) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find((e) => e.postId === postId);

      if (post) {
        post.replyCount++;
      }
    }
  },
  APPEND_POSTS_TO_FEED: (state, payload) => {
    const { data, cursor, feedTab } = payload;
    let feedKey = '';
    let cursorKey = '';

    switch (feedTab) {
      case HOME_FEED_TAB_KEYS.FOR_YOU:
        feedKey = 'homeForYouFeed';
        cursorKey = 'homeForYouFeedCursor';
        break;
      case HOME_FEED_TAB_KEYS.NEWS:
        feedKey = 'homeNewsFeed';
        cursorKey = 'homeNewsFeedCursor';
        break;
      default:
        console.error(`Unknown feedType: ${feedTab}`);
        return;
    }

    if ((state[feedKey]?.length ?? 0) > 0 && (data?.length ?? 0) > 0) {
      const feedUpdatedDates = new Map(
        state[feedKey]?.map((p) => [p.postId, p.updatedDate]) ?? []
      );

      const uniqueNewPostsToAppend = [];

      // Deduplicate posts by post id.
      // Replace existing posts in case the newly fetched post has a more recent updatedDate
      data.forEach((p) => {
        const existingUpdatedDate = feedUpdatedDates.get(p.postId);

        // If a post with the same id exists in the feed, and the new post is more recent, replace the existing post.
        if (existingUpdatedDate) {
          console.debug(
            `Post with id ${p.postId} : ${p.updatedDate} already exists in the feed with updated date ${existingUpdatedDate}.`
          );

          if (
            p.updatedDate &&
            new Date(p.updatedDate) > new Date(existingUpdatedDate)
          ) {
            console.debug(`Replacing post ${p.postId} with more recent data`);

            const existingFeedItemIndex = state[feedKey].findIndex(
              (f) => f.postId === p.postId
            );

            if (existingFeedItemIndex !== -1) {
              state[feedKey].splice(existingFeedItemIndex, 1, p);
              feedUpdatedDates.set(p.postId, p.updatedDate);
            } else {
              console.error(
                `Unexpected error occurred. Post with id ${p.postId} is suddenly not found in the feed. Skipping replacement.`
              );
            }
          }
        } else {
          uniqueNewPostsToAppend.push(p);
        }
      });

      state[feedKey] = [...state[feedKey], ...uniqueNewPostsToAppend];
    }

    state[cursorKey] = cursor;
  },
  APPEND_NEW_POST_AT_TOP: (state, post) => {
    if (state.homeForYouFeed)
      state.homeForYouFeed = [post, ...state.homeForYouFeed];
  },
  SET_FEED_LOADING: (state, isLoading) => {
    state.homeForYouFeedLoading = isLoading;
  },
  SET_NEXT_POSTS_LOADING: (state, isLoading) => {
    state.nextHomeForYouPostsLoading = isLoading;
  },
  SET_REPLIES_LOADING: (state, payload) => {
    const post = state.homeForYouFeed.find((e) => e.postId === payload.target);
    if (post) {
      post.repliesLoading = payload.isLoading;
    }
  },
  SET_REPLIES_ERROR: (state, payload) => {
    const post = state.homeForYouFeed.find((e) => e.postId === payload.target);
    if (post) {
      post.repliesError = payload.err;
      //TODO : add some toast
    }
  },
  APPEND_NEW_REPLY: (state, payload) => {
    const forYouPost = state.homeForYouFeed?.find(
      (e) => e.postId === payload.target
    );
    if (forYouPost) {
      // Finding the post
      if (!forYouPost.replies) {
        forYouPost.replies = [];
      }
      const newArray = forYouPost.replies.slice(0, 1);
      forYouPost.replies = [payload, ...newArray];
      forYouPost.replyCount++;
    }

    const newsPost = state.homeNewsFeed?.find(
      (e) => e.postId === payload.target
    );
    if (newsPost) {
      // Finding the post
      if (!newsPost.replies) {
        newsPost.replies = [];
      }
      const newArray = newsPost.replies.slice(0, 1);
      newsPost.replies = [payload, ...newArray];
      newsPost.replyCount++;
    }
  },
  UPDATE_REPLY: (state, data) => {
    if (state.homeForYouFeed) {
      // Finding the post
      const post = state.homeForYouFeed.find((e) => e.postId === data.target);
      if (post) {
        const index = post.replies.findIndex((e) => e.postId === data.postId);
        const res = [...post.replies];
        res[index] = data;

        post.replies = res;
      }
    }
  },
  UPDATE_REPLY_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const post = state.homeForYouFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const replyToUpdate = post.replies.find(
      (r) => r.postId === payload.replyId
    );

    if (!replyToUpdate) return;

    const newLikedState = !payload.replyIsLiked;

    replyToUpdate.isLiked = newLikedState;
    replyToUpdate.likeCount = newLikedState
      ? (replyToUpdate.likeCount += 1)
      : (replyToUpdate.likeCount -= 1);
  },
  ROLLBACK_REPLY_LIKE: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const post = state.homeForYouFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const replyToUpdate = post.replies.find(
      (r) => r.postId === payload.replyId
    );

    if (!replyToUpdate) return;

    replyToUpdate.isLiked = payload.replyIsLiked;
    replyToUpdate.likeCount = payload.replyLikeCount;
  },
  INCREMENT_REPLY_REPLY_COUNT: (state, payload) => {
    if (state.homeForYouFeed) {
      // Finding the post
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );
      if (post) {
        const index = post.replies.findIndex(
          (e) => e.postId === payload.replyId
        );

        post.replies[index].replyCount++;
      }
    }
  },
  UPDATE_POST_LIKE: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );

      if (post) {
        post.isLiked = payload.isLiked;
        post.likeCount = payload.likeCount;
      }
    }
  },
  UPDATE_POST_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const postToUpdate = state.homeForYouFeed.find(
      (p) => p.postId === payload.postId
    );

    if (!postToUpdate) return;

    const newLikedState = !payload.isLiked;

    postToUpdate.isLiked = newLikedState;

    postToUpdate.likeCount = newLikedState
      ? (postToUpdate.likeCount += 1)
      : (postToUpdate.likeCount -= 1);
  },
  ROLLBACK_POST_LIKE: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const postToUpdate = state.homeForYouFeed.find(
      (p) => p.postId === payload.postId
    );

    if (!postToUpdate) return;

    postToUpdate.isLiked = payload.isLiked;
    postToUpdate.likeCount = payload.likeCount;
  },
  UPDATE_POST_FAVORITE: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );

      if (post) {
        post.isFavorited = payload.isFavorited;
      }
    }
  },
  /// Nested Replies
  SET_NESTED_REPLIES: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );

      if (post) {
        const reply = post.replies.find((e) => e.postId === payload.replyId);

        if (reply) {
          reply.replies = payload.replies;
        }
      }
    }
  },
  APPEND_NESTED_REPLY_BOTTOM: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );

      if (post) {
        const reply = post.replies.find((e) => e.postId === payload.replyId);

        if (reply) {
          reply.replies = [...reply.replies, ...payload.replies];
        }
      }
    }
  },
  APPEND_NESTED_REPLY_TOP: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );

      if (post) {
        const reply = post.replies.find(
          (e) => e.postId === payload.data.target
        );

        if (reply) {
          if (!reply.replies) {
            reply.replies = [payload.data];
          } else {
            reply.replies = [payload.data, ...reply.replies];
          }
        }
      }
    }
  },
  UPDATE_NESTED_REPLY: (state, payload) => {
    if (state.homeForYouFeed) {
      // Finding the post
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );
      if (post) {
        const reply = post.replies.find(
          (e) => e.postId === payload.data.target
        );

        if (reply) {
          const index = reply.replies.findIndex(
            (e) => e.postId === payload.data.postId
          );
          const res = [...reply.replies];
          res[index] = payload.data;

          reply.replies = res;
        }
      }
    }
  },
  UPDATE_NESTED_REPLY_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const post = state.homeForYouFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const reply = post.replies.find((r) => r.postId === payload.replyId);

    if (!reply) return;

    const nestedReplyToUpdate = reply.replies.find(
      (nr) => nr.postId === payload.nestedReplyId
    );

    if (!nestedReplyToUpdate) return;

    const newLikedState = !payload.nestedReplyIsLiked;

    nestedReplyToUpdate.isLiked = newLikedState;

    nestedReplyToUpdate.likeCount = newLikedState
      ? (nestedReplyToUpdate.likeCount += 1)
      : (nestedReplyToUpdate.likeCount -= 1);
  },
  ROLLBACK_NESTED_REPLY_LIKE: (state, payload) => {
    if (!state.homeForYouFeed) return;

    const post = state.homeForYouFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const reply = post.replies.find((r) => r.postId === payload.replyId);

    if (!reply) return;

    const nestedReplyToUpdate = reply.replies.find(
      (nr) => nr.postId === payload.nestedReplyId
    );

    if (!nestedReplyToUpdate) return;

    nestedReplyToUpdate.isLiked = payload.nestedReplyIsLiked;
    nestedReplyToUpdate.likeCount = payload.nestedReplyLikeCount;
  },
  INCREMENT_NESTED_REPLY_REPLY_COUNT: (state, payload) => {
    if (state.homeForYouFeed) {
      // Finding the post
      const post = state.homeForYouFeed.find(
        (e) => e.postId === payload.postId
      );
      if (post) {
        const reply = post.replies.find((e) => e.postId === payload.replyId);

        if (reply) {
          const nestedReply = reply.replies.find(
            (e) => e.postId === payload.nestedReplyId
          );
          if (nestedReply) nestedReply.replyCount++;
        }
      }
    }
  },
  DELETE_USER_POST_SUCCESS: (state, data) => {
    if (state.homeForYouFeed) {
      const postIndex = state.homeForYouFeed.findIndex(
        (e) => e.postId === data
      );
      if (postIndex !== -1) {
        state.homeForYouFeed.splice(postIndex, 1);
      }
    }
  },
  DELETE_USER_POST_ERROR: (state, error) => {
    state.deleteUserPost.error = error;
  },
  DELETE_POST_REPLY_SUCCESS: (state, payload) => {
    if (state.homeForYouFeed) {
      const postIndex = state.homeForYouFeed.findIndex(
        (p) => p.postId === payload.postId
      );

      if (postIndex !== -1) {
        const replyIndex = state.homeForYouFeed[postIndex].replies.findIndex(
          (r) => r.postId === payload.replyId
        );

        if (replyIndex !== -1) {
          // Decrementing reply count for the post
          state.homeForYouFeed[postIndex].replyCount--;
          state.homeForYouFeed[postIndex].replies.splice(replyIndex, 1);
        }
      }
    }
  },
  DELETE_POST_REPLY_ERROR: (state, error) => {
    state.deleteUserPost.error = error;
  },
  DELETE_POST_NESTED_REPLY_SUCCESS: (state, payload) => {
    if (state.homeForYouFeed) {
      const post = state.homeForYouFeed.find(
        (p) => p.postId === payload.postId
      );

      if (post) {
        const reply = post.replies.find((r) => r.postId === payload.replyId);

        if (reply) {
          const nestedReplyIndex = reply.replies.findIndex(
            (nr) => nr.postId === payload.nestedReplyId
          );

          if (nestedReplyIndex !== -1) {
            // Decrementing reply count for the post
            reply.replyCount--;
            reply.replies.splice(nestedReplyIndex, 1);
          }
        }
      }
    }
  },

  // News feed
  GET_NEWS_FEED_START: (state) => {
    state.homeNewsFeed = [];
    state.homeNewsFeedError = null;
    state.homeNewsFeedLoading = true;
  },
  GET_NEWS_FEED_SUCCESS: (state, result) => {
    const { data, cursor } = result;

    state.homeNewsFeedCursor = cursor;
    state.homeNewsFeed = data;
    state.homeNewsFeedLoading = false;
  },
  SET_NEWS_FEED_ERROR: (state, error) => {
    state.homeNewsFeedError = error;
    state.homeNewsFeedLoading = false;
  },
  SET_NEXT_NEWS_POSTS_LOADING: (state, value) => {
    state.nextHomeNewsPostsLoading = value;
  },
  APPEND_NEW_NEWS_REPLY: (state, payload) => {
    if (state.homeNewsFeed) {
      // Finding the post
      const post = state.homeNewsFeed.find((e) => e.postId === payload.target);
      if (post) {
        if (!post.replies) {
          post.replies = [];
        }
        const newArray = post.replies.slice(0, 1);
        post.replies = [payload, ...newArray];
        post.replyCount++;
      }
    }
  },
  SET_NEWS_REPLIES_ERROR: (state, payload) => {
    const post = state.homeNewsFeed.find((e) => e.postId === payload.target);
    if (post) {
      post.repliesError = payload.err;
    }
  },
  UPDATE_NEWS_REPLY: (state, data) => {
    if (state.homeNewsFeed) {
      // Finding the post
      const post = state.homeNewsFeed.find((e) => e.postId === data.target);
      if (post) {
        const index = post.replies.findIndex((e) => e.postId === data.postId);
        const res = [...post.replies];
        res[index] = data;

        post.replies = res;
      }
    }
  },
  UPDATE_NEWS_POST_LIKE: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);

      if (post) {
        post.isLiked = payload.isLiked;
        post.likeCount = payload.likeCount;
      }
    }
  },
  UPDATE_NEWS_REPLY_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const post = state.homeNewsFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const replyToUpdate = post.replies.find(
      (r) => r.postId === payload.replyId
    );

    if (!replyToUpdate) return;

    const newLikedState = !payload.replyIsLiked;

    replyToUpdate.isLiked = newLikedState;
    replyToUpdate.likeCount = newLikedState
      ? (replyToUpdate.likeCount += 1)
      : (replyToUpdate.likeCount -= 1);
  },
  ROLLBACK_NEWS_REPLY_LIKE: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const post = state.homeNewsFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const replyToUpdate = post.replies.find(
      (r) => r.postId === payload.replyId
    );

    if (!replyToUpdate) return;

    replyToUpdate.isLiked = payload.replyIsLiked;
    replyToUpdate.likeCount = payload.replyLikeCount;
  },
  UPDATE_NEWS_POST_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const postToUpdate = state.homeNewsFeed.find(
      (p) => p.postId === payload.postId
    );

    if (!postToUpdate) return;

    const newLikedState = !payload.isLiked;

    postToUpdate.isLiked = newLikedState;
    if (newLikedState) {
      postToUpdate.likeCount += 1;
    } else {
      postToUpdate.likeCount -= 1;
    }
  },
  ROLLBACK_NEWS_POST_LIKE: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const postToUpdate = state.homeNewsFeed.find(
      (p) => p.postId === payload.postId
    );

    if (!postToUpdate) return;

    postToUpdate.isLiked = payload.isLiked;
    postToUpdate.likeCount = payload.likeCount;
  },
  UPDATE_NEWS_POST_FAVORITE: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);

      if (post) {
        post.isFavorited = payload.isFavorited;
      }
    }
  },
  // Nested replies
  SET_NESTED_NEWS_REPLIES: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);

      if (post) {
        const reply = post.replies.find((e) => e.postId === payload.replyId);

        if (reply) {
          reply.replies = payload.replies;
        }
      }
    }
  },
  APPEND_NESTED_NEWS_REPLY_BOTTOM: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);

      if (post) {
        const reply = post.replies.find((e) => e.postId === payload.replyId);

        if (reply) {
          reply.replies = [...reply.replies, ...payload.replies];
        }
      }
    }
  },
  APPEND_NEWS_NESTED_REPLY_TOP: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);

      if (post) {
        const reply = post.replies.find(
          (e) => e.postId === payload.data.target
        );

        if (reply) {
          if (!reply.replies) {
            reply.replies = [payload.data];
          } else {
            reply.replies = [payload.data, ...reply.replies];
          }
        }
      }
    }
  },
  UPDATE_NESTED_NEWS_REPLY: (state, payload) => {
    if (state.homeNewsFeed) {
      // Finding the post
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);
      if (post) {
        const reply = post.replies.find(
          (e) => e.postId === payload.data.target
        );

        if (reply) {
          const index = reply.replies.findIndex(
            (e) => e.postId === payload.data.postId
          );
          const res = [...reply.replies];
          res[index] = payload.data;

          reply.replies = res;
        }
      }
    }
  },
  UPDATE_NEWS_NESTED_REPLY_LIKE_OPTIMISTIC: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const post = state.homeNewsFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const reply = post.replies.find((r) => r.postId === payload.replyId);

    if (!reply) return;

    const nestedReplyToUpdate = reply.replies.find(
      (nr) => nr.postId === payload.nestedReplyId
    );

    if (!nestedReplyToUpdate) return;

    const newLikedState = !payload.nestedReplyIsLiked;

    nestedReplyToUpdate.isLiked = newLikedState;

    nestedReplyToUpdate.likeCount = newLikedState
      ? (nestedReplyToUpdate.likeCount += 1)
      : (nestedReplyToUpdate.likeCount -= 1);
  },
  ROLLBACK_NEWS_NESTED_REPLY_LIKE: (state, payload) => {
    if (!state.homeNewsFeed) return;

    const post = state.homeNewsFeed.find((p) => p.postId === payload.postId);

    if (!post) return;

    const reply = post.replies.find((r) => r.postId === payload.replyId);

    if (!reply) return;

    const nestedReplyToUpdate = reply.replies.find(
      (nr) => nr.postId === payload.nestedReplyId
    );

    if (!nestedReplyToUpdate) return;

    nestedReplyToUpdate.isLiked = payload.nestedReplyIsLiked;
    nestedReplyToUpdate.likeCount = payload.nestedReplyLikeCount;
  },
  DELETE_NEWS_POST_REPLY_SUCCESS: (state, payload) => {
    if (state.homeNewsFeed) {
      const postIndex = state.homeNewsFeed.findIndex(
        (p) => p.postId === payload.postId
      );

      if (postIndex !== -1) {
        const replyIndex = state.homeNewsFeed[postIndex].replies.findIndex(
          (r) => r.postId === payload.replyId
        );

        if (replyIndex !== -1) {
          // Decrementing reply count for the post
          state.homeNewsFeed[postIndex].replyCount--;
          state.homeNewsFeed[postIndex].replies.splice(replyIndex, 1);
        }
      }
    }
  },
  DELETE_NEWS_POST_NESTED_REPLY_SUCCESS: (state, payload) => {
    if (state.homeNewsFeed) {
      const post = state.homeNewsFeed.find((p) => p.postId === payload.postId);

      if (post) {
        const reply = post.replies.find((r) => r.postId === payload.replyId);

        if (reply) {
          const nestedReplyIndex = reply.replies.findIndex(
            (nr) => nr.postId === payload.nestedReplyId
          );

          if (nestedReplyIndex !== -1) {
            // Decrementing reply count for the post
            reply.replyCount--;
            reply.replies.splice(nestedReplyIndex, 1);
          }
        }
      }
    }
  },
  INCREMENT_NEWS_REPLY_REPLY_COUNT: (state, payload) => {
    if (state.homeNewsFeed) {
      // Finding the post
      const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);
      if (post) {
        const index = post.replies.findIndex(
          (e) => e.postId === payload.replyId
        );

        post.replies[index].replyCount++;
      }
    }
  },
  UPDATE_NEWS_REPLY_AFTER_EDIT: (state, payload) => {
    const post = state.homeNewsFeed.find((e) => e.postId === payload.postId);
    if (post) {
      const replyIndex = post.replies.findIndex(
        (e) => e.postId === payload.data.target
      );

      if (replyIndex !== -1) {
        if (post.replies[replyIndex].replyCount)
          payload.data.replyCount = post.replies[replyIndex].replyCount;
        if (post.replies[replyIndex].replies)
          payload.data.replies = post.replies[replyIndex].replies;

        post.replies.splice(replyIndex, 1, payload.data);
      }
    } else {
      const parentPost = state.homeNewsFeed.find((p) =>
        p.replies?.some((r) => r.postId === payload.postId)
      );
      if (!parentPost) return;
      const parentPostIndex = state.homeNewsFeed.findIndex((p) =>
        p.replies?.some((r) => r.postId === payload.postId)
      );
      const reply = parentPost?.replies?.find(
        (r) => r.postId === payload.postId
      );
      if (!reply) return;
      const replyIndex = parentPost?.replies?.findIndex(
        (r) => r.postId === payload.postId
      );
      const nestedReplyIndex = reply.replies.findIndex(
        (nr) => nr.postId === payload.data.postId
      );
      if (nestedReplyIndex !== -1) {
        payload.data.target = payload.postId;
        reply.replies[nestedReplyIndex] = payload.data;
        parentPost.replies[replyIndex] = reply;
        state.homeNewsFeed.splice(parentPostIndex, 1, parentPost);
      }
    }
  },
};
